import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button, Separator } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { CoverImage } from '@/components/CoverImage'

export default function FestivalsLosingApplicantsPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/festivals-losing-applicants', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: '5 Ways Classical Music Festivals Lose Applicants Online',
    description:
      'Common website and funnel mistakes that cost festivals qualified applicants—and how to fix them. Learn mobile optimization, application funnels, and more.',
    url: '/blog/festivals-losing-applicants',
    canonical: '/blog/festivals-losing-applicants',
    type: 'article',
    breadcrumbs,
    article: {
      publishedTime: '2025-01-15',
      author: 'Garrett John Law',
      section: 'Festival Strategy',
      tags: ['festival websites', 'application funnels', 'enrollment growth', 'digital strategy'],
    },
    schema: createArticleSchema({
      headline: '5 Ways Classical Music Festivals Lose Applicants Online',
      description: 'Common website and funnel mistakes that cost festivals qualified applicants—and how to fix them.',
      datePublished: '2025-01-15',
      url: '/blog/festivals-losing-applicants',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-3xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4">
        <Link to="/blog" className="text-primary text-sm hover:underline">
          &larr; Back to Blog
        </Link>
        <h1 className="text-fluid-h1">5 Ways Classical Music Festivals Lose Applicants Online</h1>
        <div className="text-muted-foreground flex items-center gap-4 text-sm">
          <time dateTime="2025-01-15">January 15, 2025</time>
          <span>&middot;</span>
          <span>8 min read</span>
        </div>
        <p className="text-fluid-h4 text-muted-foreground">
          Common website and funnel mistakes that cost festivals qualified applicants—and how to fix them.
        </p>
      </header>

      <CoverImage
        slug="festivals-losing-applicants"
        alt="A music festival audience with raised hands at an outdoor stage"
      />

      <Separator />

      {/* Introduction */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <p className="text-lg">
          You have a world-class faculty. Stunning performance venues. A transformative curriculum. Students who attend
          your festival rave about it.
        </p>
        <p className="text-lg">So why aren&apos;t more people applying?</p>
        <p>
          The problem isn&apos;t your program. It&apos;s your digital presence. Most festival websites were built 5-10
          years ago and haven&apos;t been updated to meet modern standards—or modern student expectations.
        </p>
        <p>
          The result? Talented students visit your site, get confused or frustrated, and apply to the next festival
          instead.
        </p>
        <p>
          After working with classical music festivals like Chroma International Music Festival and reviewing hundreds
          of festival websites, I&apos;ve identified five critical mistakes that cost festivals qualified applicants.
          The good news? They&apos;re all fixable.
        </p>
      </section>

      <Separator />

      {/* Problem 1 */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">1. Your Website Doesn&apos;t Work on Mobile</h2>

        <div className="bg-destructive/10 border-destructive border-l-4 px-6 py-4">
          <p className="text-destructive font-semibold">The Problem:</p>
          <p className="text-muted-foreground mt-2">
            Gen Z students (your primary applicant pool) browse almost exclusively on phones. If your website
            doesn&apos;t work seamlessly on mobile, they won&apos;t struggle with it—they&apos;ll just leave.
          </p>
        </div>

        <div className="space-y-4">
          <p className="font-semibold">What This Looks Like:</p>
          <ul className="text-muted-foreground list-disc space-y-2 pl-6">
            <li>Text is too small to read without zooming</li>
            <li>Buttons are too close together to tap accurately</li>
            <li>Application forms don&apos;t fit on the screen</li>
            <li>Images load slowly or break the layout</li>
            <li>Navigation menus are impossible to use on a phone</li>
          </ul>
        </div>

        <div className="bg-primary/10 border-primary border-l-4 px-6 py-4">
          <p className="text-primary font-semibold">The Fix:</p>
          <div className="text-muted-foreground mt-2 space-y-3">
            <p>
              <strong>Immediate:</strong> Test your website on your phone right now. Can you easily find the application
              link? Can you read the faculty list? If not, prioritize a mobile-responsive redesign.
            </p>
            <p>
              <strong>Long-term:</strong> Build your next website with a &quot;mobile-first&quot; approach. Design for
              phones first, then adapt for desktop—not the other way around.
            </p>
            <p>
              <strong>Test:</strong> Use Google&apos;s Mobile-Friendly Test tool to see how your site performs on mobile
              devices.
            </p>
          </div>
        </div>

        <div className="bg-muted/5 border-border rounded-lg border p-6">
          <p className="text-muted-foreground text-sm">
            <strong>Real Example:</strong> One festival we analyzed had a beautiful desktop site, but on mobile, the
            &quot;Apply Now&quot; button was hidden below the fold and required 4 clicks to find. After redesigning for
            mobile-first, their mobile application completion rate increased by 43%.
          </p>
        </div>
      </section>

      <Separator />

      {/* Problem 2 */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">2. Your Application Process Is a Confusing Multi-Step Nightmare</h2>

        <div className="bg-destructive/10 border-destructive border-l-4 px-6 py-4">
          <p className="text-destructive font-semibold">The Problem:</p>
          <p className="text-muted-foreground mt-2">
            Students have to fill out a form, then email documents separately, then pay via a different link, then
            confirm via another email. Half of them give up before finishing.
          </p>
        </div>

        <div className="space-y-4">
          <p className="font-semibold">What This Looks Like:</p>
          <ul className="text-muted-foreground list-disc space-y-2 pl-6">
            <li>&quot;Fill out this form, then email your repertoire list to applications@festival.com&quot;</li>
            <li>&quot;After submitting, check your email for payment instructions&quot;</li>
            <li>&quot;Send your recommendation letters to a different email address than your application&quot;</li>
            <li>Multiple disconnected systems that don&apos;t talk to each other</li>
            <li>No way to see application status (&quot;Did they receive my documents?&quot;)</li>
          </ul>
        </div>

        <div className="bg-primary/10 border-primary border-l-4 px-6 py-4">
          <p className="text-primary font-semibold">The Fix:</p>
          <div className="text-muted-foreground mt-2 space-y-3">
            <p>
              <strong>Immediate:</strong> Map out your entire application process from the student&apos;s perspective.
              How many steps are there? How many different tools do they need to use? Where are the friction points?
            </p>
            <p>
              <strong>Long-term:</strong> Implement an integrated application funnel where students can:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Fill out one comprehensive form</li>
              <li>Upload all documents in one place</li>
              <li>Pay immediately after submitting</li>
              <li>Receive instant confirmation</li>
              <li>Check their application status anytime</li>
            </ul>
          </div>
        </div>

        <div className="bg-muted/5 border-border rounded-lg border p-6">
          <p className="text-muted-foreground text-sm">
            <strong>Real Example:</strong> Chroma Festival went from a 6-step application process (form &rarr; email
            documents &rarr; pay separately &rarr; confirm) to a single integrated flow. Application completion rate
            went from 62% to 89%.
          </p>
        </div>
      </section>

      <Separator />

      {/* Problem 3 */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">3. You Have No Idea Where Applicants Drop Off</h2>

        <div className="bg-destructive/10 border-destructive border-l-4 px-6 py-4">
          <p className="text-destructive font-semibold">The Problem:</p>
          <p className="text-muted-foreground mt-2">
            You know 200 people inquired and 80 applied. But you have no idea what happened to the other 120. Did they
            lose interest? Did they find the process confusing? Did your emails go to spam? You&apos;ll never know.
          </p>
        </div>

        <div className="space-y-4">
          <p className="font-semibold">What This Looks Like:</p>
          <ul className="text-muted-foreground list-disc space-y-2 pl-6">
            <li>No tracking of who visited your website vs. who inquired</li>
            <li>No tracking of who inquired vs. who started applications</li>
            <li>No tracking of who started vs. who completed applications</li>
            <li>No idea which marketing channels drive the most applications</li>
            <li>
              Every year you repeat the same process hoping for different results (but you have no data to improve)
            </li>
          </ul>
        </div>

        <div className="bg-primary/10 border-primary border-l-4 px-6 py-4">
          <p className="text-primary font-semibold">The Fix:</p>
          <div className="text-muted-foreground mt-2 space-y-3">
            <p>
              <strong>Immediate:</strong> Set up basic Google Analytics on your website to track:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>How many people visit your site</li>
              <li>Which pages they visit most</li>
              <li>Where they&apos;re coming from (Google, Instagram, email, etc.)</li>
              <li>How long they stay on each page</li>
            </ul>
            <p className="mt-3">
              <strong>Long-term:</strong> Implement a complete application funnel with tracking at every step:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                <strong>Stage 1:</strong> Website visitors (total traffic)
              </li>
              <li>
                <strong>Stage 2:</strong> Inquiries (submitted inquiry form)
              </li>
              <li>
                <strong>Stage 3:</strong> Application started (began filling out application)
              </li>
              <li>
                <strong>Stage 4:</strong> Application submitted (completed application)
              </li>
              <li>
                <strong>Stage 5:</strong> Payment received (enrolled student)
              </li>
            </ul>
            <p className="mt-3">
              This lets you calculate conversion rates at every step and identify exactly where applicants are dropping
              off.
            </p>
          </div>
        </div>

        <div className="bg-muted/5 border-border rounded-lg border p-6">
          <p className="text-muted-foreground text-sm">
            <strong>Real Example:</strong> One festival discovered that 45% of applicants were dropping off at the
            &quot;upload recommendation letters&quot; step. After simplifying it (allow recommenders to upload directly
            via a link), completion rates improved immediately.
          </p>
        </div>
      </section>

      <Separator />

      {/* Problem 4 */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">4. Your Website Loads Slower Than a Brahms Symphony</h2>

        <div className="bg-destructive/10 border-destructive border-l-4 px-6 py-4">
          <p className="text-destructive font-semibold">The Problem:</p>
          <p className="text-muted-foreground mt-2">
            Your site takes 10+ seconds to load. Studies show 53% of mobile users abandon sites that take longer than 3
            seconds. You&apos;re losing half your visitors before they even see your content.
          </p>
        </div>

        <div className="space-y-4">
          <p className="font-semibold">Common Causes:</p>
          <ul className="text-muted-foreground list-disc space-y-2 pl-6">
            <li>Unoptimized images (a 5MB photo that should be 200KB)</li>
            <li>Too many plugins or scripts loading on every page</li>
            <li>Old, bloated website platform (looking at you, outdated WordPress themes)</li>
            <li>No caching or content delivery network (CDN)</li>
            <li>Autoplaying videos on the homepage</li>
          </ul>
        </div>

        <div className="bg-primary/10 border-primary border-l-4 px-6 py-4">
          <p className="text-primary font-semibold">The Fix:</p>
          <div className="text-muted-foreground mt-2 space-y-3">
            <p>
              <strong>Immediate:</strong> Test your site speed:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                Go to{' '}
                <a
                  href="https://pagespeed.web.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  PageSpeed Insights
                </a>
              </li>
              <li>Enter your website URL</li>
              <li>Check your mobile score (should be 85+)</li>
              <li>Follow the recommendations to compress images and reduce scripts</li>
            </ul>
            <p className="mt-3">
              <strong>Long-term:</strong> Rebuild on a modern, fast platform like Next.js that prioritizes speed. Use a
              CDN to serve images quickly worldwide. Compress all images before uploading.
            </p>
          </div>
        </div>

        <div className="bg-muted/5 border-border rounded-lg border p-6">
          <p className="text-muted-foreground text-sm">
            <strong>Real Example:</strong> One festival&apos;s homepage had a 15MB autoplay video. Load time: 18 seconds
            on mobile. After removing the video and optimizing images, load time dropped to 2.1 seconds. Bounce rate
            decreased by 31%.
          </p>
        </div>
      </section>

      <Separator />

      {/* Problem 5 */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">5. You Don&apos;t Follow Up with Inquiries (So They Forget About You)</h2>

        <div className="bg-destructive/10 border-destructive border-l-4 px-6 py-4">
          <p className="text-destructive font-semibold">The Problem:</p>
          <p className="text-muted-foreground mt-2">
            Someone fills out your inquiry form in January. You manually send them an email. Then... nothing. By March
            when applications are due, they&apos;ve completely forgotten about your festival. They apply to the festival
            that kept in touch.
          </p>
        </div>

        <div className="space-y-4">
          <p className="font-semibold">What This Looks Like:</p>
          <ul className="text-muted-foreground list-disc space-y-2 pl-6">
            <li>
              Someone inquires &rarr; you send one email &rarr; they never hear from you again until you&apos;re
              panicking about low applications
            </li>
            <li>No automated follow-ups reminding them about application deadlines</li>
            <li>No nurture sequence highlighting your faculty, programs, or student experiences</li>
            <li>
              No reminders for students who started applications but didn&apos;t finish (&quot;You&apos;re 80% done—need
              help?&quot;)
            </li>
          </ul>
        </div>

        <div className="bg-primary/10 border-primary border-l-4 px-6 py-4">
          <p className="text-primary font-semibold">The Fix:</p>
          <div className="text-muted-foreground mt-2 space-y-3">
            <p>
              <strong>Immediate:</strong> Create a simple email sequence for inquiries:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Day 0:</strong> Immediate welcome email with application link
              </li>
              <li>
                <strong>Day 3:</strong> Follow-up highlighting your faculty
              </li>
              <li>
                <strong>Day 7:</strong> Student testimonial or success story
              </li>
              <li>
                <strong>Day 14:</strong> Application deadline reminder
              </li>
              <li>
                <strong>Day 21:</strong> Final reminder (&quot;Applications close in 1 week&quot;)
              </li>
            </ul>
            <p className="mt-3">Even a basic Mailchimp automation will dramatically increase your application rate.</p>
            <p className="mt-3">
              <strong>Long-term:</strong> Implement automated email sequences that:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Nurture inquiries with valuable content (not just &quot;apply now&quot;)</li>
              <li>Remind students who started applications but didn&apos;t finish</li>
              <li>Send payment reminders for students who applied but haven&apos;t paid</li>
              <li>Stay in touch with enrolled students before the program starts</li>
            </ul>
          </div>
        </div>

        <div className="bg-muted/5 border-border rounded-lg border p-6">
          <p className="text-muted-foreground text-sm">
            <strong>Real Example:</strong> One festival had 180 inquiries but only 45 applications (25% conversion).
            After implementing a 5-email nurture sequence, inquiries-to-applications increased to 42% the following
            year—same marketing budget, 76% more applications.
          </p>
        </div>
      </section>

      <Separator />

      {/* Conclusion */}
      <section className="article-section space-y-6">
        <h2 className="text-fluid-h2">The Bottom Line</h2>
        <p>
          Your festival isn&apos;t failing to attract applicants because your program isn&apos;t good enough.
          You&apos;re losing qualified students because your digital presence doesn&apos;t match the quality of your
          music-making.
        </p>
        <p>The good news? These problems are fixable:</p>
        <ul className="text-muted-foreground list-disc space-y-2 pl-6">
          <li>Mobile-responsive websites can be built in 6-8 weeks</li>
          <li>Application funnels can be integrated and automated</li>
          <li>Analytics tracking is straightforward to set up</li>
          <li>Page speed can be optimized with image compression and modern platforms</li>
          <li>Email sequences can be automated with tools like Mailchimp or ConvertKit</li>
        </ul>
        <p className="mt-6 text-lg font-medium">
          Stop losing applicants to fixable problems. Start tracking, testing, and optimizing—and watch your enrollment
          grow.
        </p>
      </section>

      <Separator />

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-8 text-center">
        <h3 className="text-2xl font-semibold">Want Help Fixing These Issues?</h3>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          We specialize in building modern websites and application funnels for classical music festivals. Schedule a
          free 15-minute digital audit to see where you&apos;re losing applicants—and how to fix it.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <Button variant="default" size="lg" className="cursor-pointer">
                Book a Free Call &rarr;
              </Button>
            </a>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/services/festival-websites">
              <Button variant="outline" size="lg" className="cursor-pointer">
                See Our Festival Packages
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Separator />

      {/* Author Bio */}
      <section className="border-border border-t pt-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold">About the Author</p>
            <p className="text-muted-foreground mt-2">
              <strong>Garrett John Law</strong> is a musician and software engineer who builds digital systems for
              classical music festivals. An Interlochen alum and CIM graduate, he currently serves as organist and
              choirmaster at Holy Trinity Episcopal Church in Los Angeles.
            </p>
            <div className="mt-4">
              <Link to="/about" className="text-primary text-sm hover:underline">
                Learn more about Fugue Lab &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}
